import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// JWT session token (issued by /api/auth/*). Replaces the old localStorage
// role-switcher: all requests are now server-verified.
export const TOKEN_KEY = 'fmla_jwt_v1';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// `X-Session-Bind` asks the API to bind every token it mints to this browser:
// the token then only works alongside an httpOnly cookie that JavaScript cannot
// read, so a token copied out of localStorage by an XSS is useless elsewhere.
// `withCredentials` is what carries that cookie when the API is a different
// origin than the app (local dev; production serves both from one domain).
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-Session-Bind': '1' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    // Session expired / invalid: clear and notify app (skip login attempts themselves)
    if (status === 401 && getToken() && !url.includes('/auth/login')) {
      setToken('');
      window.dispatchEvent(new CustomEvent('auth-expired'));
    }
    return Promise.reject(err);
  }
);

// Opens a protected file (PDF/upload) in the IN-APP viewer using the JWT.
// PDFs: fetched as server-rendered PNG pages — Chrome's PDF plugin refuses to
// run inside sandboxed iframes, so images are the only bulletproof preview.
// Other files: fetched as a blob. The viewer offers a Download button too.
export async function openAuthedFile(path, filename) {
  if (path.endsWith('/pdf')) {
    try {
      const r = await api.get(`${path}/preview`);
      window.dispatchEvent(new CustomEvent('fmla:view-file', {
        detail: { pages: r.data.pages, filename: filename || 'document.pdf', type: 'application/pdf', downloadPath: path },
      }));
      return;
    } catch {
      // fall through to raw blob (still lets user download)
    }
  }
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const type = res.data?.type || '';
  const fallbackExt = type.includes('pdf') ? '.pdf' : type.includes('png') ? '.png' : type.includes('jpeg') ? '.jpg' : '';
  window.dispatchEvent(new CustomEvent('fmla:view-file', {
    detail: { url, filename: filename || `document${fallbackExt}`, type },
  }));
}

// Normalizes API error payloads (including FastAPI 422 validation arrays)
// into a human-readable string safe for toasts.
export function errText(err, fallback = 'Something went wrong') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : '';
        return field ? `${field}: ${d?.msg || 'invalid'}` : (d?.msg || 'invalid');
      })
      .join('; ');
  }
  if (detail && typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  // Prefer the caller's (often localized) fallback over the raw Axios message —
  // an AxiosError always has a `message` like "Network Error" / "Request failed
  // with status code 504", which is English and unhelpful to a patient (GAPS F21).
  return fallback;
}

export default api;
